"use client";

import {
  Listbox as HeadlessListbox,
  ListboxButton as HeadlessListboxButton,
  ListboxOption as HeadlessListboxOption,
  ListboxOptions as HeadlessListboxOptions,
  ListboxSelectedOption as HeadlessListboxSelectedOption,
  Transition as HeadlessTransition,
  type ListboxOptionProps as HeadlessListboxOptionProps,
  type ListboxProps as HeadlessListboxProps,
} from "@headlessui/react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import React, { Fragment } from "react";
import { RefCallBack } from "react-hook-form";

export function Listbox<T>({
  className,
  placeholder,
  autoFocus,
  "aria-label": ariaLabel,
  children: options,
  inputRef,
  inputName,
  ...props
}: {
  className?: string;
  placeholder?: React.ReactNode;
  autoFocus?: boolean;
  "aria-label"?: string;
  children?: React.ReactNode;
  inputName?: string;
  inputRef?: RefCallBack;
} & Omit<HeadlessListboxProps<typeof Fragment, T>, "multiple">) {
  return (
    <HeadlessListbox {...props} multiple={false}>
      <HeadlessListboxButton
        ref={inputRef}
        name={inputName}
        autoFocus={autoFocus}
        data-slot="control"
        aria-label={ariaLabel}
        className={clsx([
          className,

          // Basic layout
          "group relative block w-full",

          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          "before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow",

          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          "dark:before:hidden",

          // Hide default focus styles
          "focus:outline-none",

          // Focus ring
          "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:after:data-[focus]:ring-2 sm:after:data-[focus]:ring-blue-500",

          // Disabled state
          "data-[disabled]:opacity-50 before:data-[disabled]:bg-zinc-950/5 before:data-[disabled]:shadow-none",
        ])}>
        <HeadlessListboxSelectedOption
          as="span"
          options={options}
          placeholder={
            placeholder && (
              <span className="block truncate text-zinc-500">
                {placeholder}
              </span>
            )
          }
          className={clsx([
            // Basic layout
            "relative block w-full appearance-none rounded-md py-[calc(theme(spacing[2.5])-1px)]",

            // Set minimum height for when no value is selected
            "min-h-11 sm:min-h-9",

            // Horizontal padding
            "pl-[calc(theme(spacing[3.5])-1px)] pr-[calc(theme(spacing.7)-1px)] sm:pl-[calc(theme(spacing.3)-1px)]",

            // Typography
            "text-left text-base/6 text-zinc-950 placeholder:text-zinc-400 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]",

            // Border
            "border border-zinc-950/10 group-data-[active]:border-zinc-950/20 group-data-[hover]:border-zinc-950/20 dark:border-white/10 dark:group-data-[active]:border-white/20 dark:group-data-[hover]:border-white/20",

            // Background color
            "bg-transparent dark:bg-white/5",

            // Invalid state
            "group-data-[invalid]:border-red-500 group-data-[invalid]:group-data-[hover]:border-red-500 group-data-[invalid]:dark:border-red-600 group-data-[invalid]:data-[hover]:dark:border-red-600",

            // Disabled state
            "group-data-[disabled]:border-zinc-950/20 group-data-[disabled]:opacity-100 group-data-[disabled]:dark:border-white/15 group-data-[disabled]:dark:bg-white/[2.5%] dark:data-[hover]:group-data-[disabled]:border-white/15",
          ])}
        />
        <span className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronDownIcon className="size-5 text-gray-400" />
        </span>
      </HeadlessListboxButton>
      <HeadlessTransition
        as={Fragment}
        leave="transition-opacity duration-100 ease-in pointer-events-none"
        leaveFrom="opacity-100"
        leaveTo="opacity-0">
        <HeadlessListboxOptions
          as="div"
          anchor={{ to: "bottom end" }}
          className={clsx(
            "mt-1 w-[var(--button-width)] isolate rounded-md border border-white/5 bg-white/5 [--anchor-gap:var(--spacing-1)] focus:outline-none",

            // Handle scrolling when menu won't fit in viewport
            "overflow-y-scroll overscroll-contain",

            // Popover background
            "bg-white/90 backdrop-blur-md dark:bg-zinc-800/75",

            // Shadows
            "shadow-lg ring-1 ring-zinc-950/10 dark:ring-inset dark:ring-white/10",
          )}>
          {options}
        </HeadlessListboxOptions>
      </HeadlessTransition>
    </HeadlessListbox>
  );
}

export function ListboxOption<T>({
  children,
  className,
  ...props
}: { children?: React.ReactNode } & HeadlessListboxOptionProps<"div", T>) {
  const sharedClasses = clsx(
    "group flex cursor-default items-center gap-2 rounded-lg select-none data-[focus]:bg-white/10",

    // Base
    // 'flex min-w-0 items-center z-[80]',

    // Icons
    "[&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-zinc-500 [&>[data-slot=icon]]:group-data-[focus]/option:text-white sm:[&>[data-slot=icon]]:size-4 forced-colors:[&>[data-slot=icon]]:text-[CanvasText] forced-colors:[&>[data-slot=icon]]:group-data-[focus]/option:text-[Canvas]",

    // Avatars
    "[&>[data-slot=avatar]]:size-6 sm:[&>[data-slot=avatar]]:size-5",
  );

  return (
    <HeadlessListboxOption as={Fragment} {...props}>
      {({ selectedOption }) => {
        if (selectedOption) {
          return (
            <div className={clsx(className, sharedClasses)}>{children}</div>
          );
        }

        return (
          <div
            className={clsx(
              // Basic layout
              "z-[80] group/option grid cursor-default grid-cols-[theme(spacing.5),1fr] items-baseline gap-x-1.5 rounded-md mx-1 mt-1 py-2.5 pl-2.5 pr-3.5 sm:grid-cols-[theme(spacing.4),1fr] sm:py-1.5 sm:pl-2 sm:pr-3",

              // Typography
              "text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]",

              // Focus
              "outline-none data-[focus]:bg-blue-500 data-[focus]:text-white",

              // Forced colors mode
              "forced-color-adjust-none forced-colors:data-[focus]:bg-[Highlight] forced-colors:data-[focus]:text-[HighlightText]",

              // Disabled
              "data-[disabled]:opacity-50",
            )}>
            <span className={clsx(className, sharedClasses, "col-end-1")}>
              {children}
            </span>
            <CheckIcon className="col-end-[none] relative hidden size-5 self-center group-data-[selected]/option:inline sm:size-4" />
          </div>
        );
      }}
    </HeadlessListboxOption>
  );
}

export function ListboxLabel({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"span">) {
  return (
    <span
      className={clsx(
        className,
        "ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0",
      )}
      {...props}
    />
  );
}

export function ListboxDescription({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<"span">) {
  return (
    <span
      className={clsx(
        className,
        "flex flex-1 overflow-hidden text-zinc-500 before:w-2 before:min-w-0 before:shrink group-data-[focus]/option:text-white dark:text-zinc-400",
      )}
      {...props}>
      <span className="flex-1 truncate">{children}</span>
    </span>
  );
}
